import QRCode from 'qrcode';
import { Platform } from 'react-native';

import {
  garageVehiclePath,
  garageVehicleTitle,
  type GarageVehicle,
} from '@/src/features/garage/garage-service';
import { copyTextToClipboard } from '@/src/utils/clipboard';

const siteOrigin = 'https://frienzzi.com';
const logoURL = `${siteOrigin}/email/frienzzi-logo.png`;

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function printLoadingHtml() {
  return `<!doctype html>
<html>
  <head>
    <title>Preparing Frienzzi Garage QR code</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        align-items: center;
        background: #f9f8fb;
        color: #1a0c3e;
        display: flex;
        font-family: Arial, Helvetica, sans-serif;
        justify-content: center;
        min-height: 100vh;
      }
    </style>
  </head>
  <body>Preparing QR code...</body>
</html>`;
}

function printErrorHtml(message: string) {
  return `<!doctype html>
<html>
  <head>
    <title>Frienzzi Garage QR code unavailable</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        align-items: center;
        background: #f9f8fb;
        color: #1a0c3e;
        display: flex;
        font-family: Arial, Helvetica, sans-serif;
        justify-content: center;
        min-height: 100vh;
        margin: 0;
        padding: 24px;
      }

      main {
        background: #ffffff;
        border: 1px solid #ebeaea;
        border-radius: 8px;
        max-width: 520px;
        padding: 28px;
      }

      h1 {
        font-size: 24px;
        margin: 0 0 12px;
      }

      p {
        color: #6c5ba7;
        font-size: 16px;
        line-height: 1.45;
        margin: 0;
      }
    </style>
  </head>
  <body>
    <main>
      <h1>QR code unavailable</h1>
      <p>${escapeHtml(message)}</p>
    </main>
  </body>
</html>`;
}

function placardHtml({
  ownerName,
  qrSvg,
  title,
  url,
}: {
  ownerName: string;
  qrSvg: string;
  title: string;
  url: string;
}) {
  return `<!doctype html>
<html>
  <head>
    <title>${escapeHtml(title)} QR code</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      @page {
        margin: 0.35in;
        size: letter portrait;
      }

      * {
        box-sizing: border-box;
      }

      body {
        background: #f9f8fb;
        color: #1a0c3e;
        font-family: Arial, Helvetica, sans-serif;
        margin: 0;
        min-height: 100vh;
        padding: 24px;
      }

      .placard {
        align-items: center;
        background:
          radial-gradient(circle at 12% 12%, rgba(255, 37, 142, 0.18), transparent 30%),
          radial-gradient(circle at 92% 18%, rgba(22, 143, 255, 0.16), transparent 32%),
          #ffffff;
        border: 3px solid #1a0c3e;
        border-radius: 26px;
        display: flex;
        flex-direction: column;
        gap: 22px;
        min-height: calc(100vh - 48px);
        overflow: hidden;
        padding: 38px;
        position: relative;
        text-align: center;
      }

      .stripe {
        background: linear-gradient(90deg, #ff258e, #7443f1, #168fff, #f78a63);
        height: 12px;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
      }

      .logo {
        height: auto;
        margin-top: 8px;
        max-width: 270px;
      }

      .eyebrow {
        color: #7443f1;
        font-size: 17px;
        font-weight: 900;
        letter-spacing: 0.12em;
        margin: 0;
        text-transform: uppercase;
      }

      h1 {
        color: #1a0c3e;
        font-size: 44px;
        line-height: 1.04;
        margin: 0;
        max-width: 720px;
      }

      .owner {
        color: #6c5ba7;
        font-size: 20px;
        font-weight: 800;
        margin: 0;
      }

      .qr-wrap {
        background: #ffffff;
        border: 4px solid #ff258e;
        border-radius: 24px;
        box-shadow: 0 20px 50px rgba(26, 12, 62, 0.16);
        padding: 24px;
      }

      .qr-wrap svg {
        display: block;
        height: min(48vw, 420px);
        width: min(48vw, 420px);
      }

      .cta {
        color: #1a0c3e;
        font-size: 28px;
        font-weight: 900;
        margin: 0;
      }

      .url {
        background: #fff7fc;
        border: 2px solid #ebeaea;
        border-radius: 999px;
        color: #7443f1;
        font-size: 18px;
        font-weight: 900;
        margin: 0;
        max-width: 100%;
        overflow-wrap: anywhere;
        padding: 12px 22px;
      }

      .footer {
        color: #6c5ba7;
        font-size: 15px;
        font-weight: 800;
        margin-top: auto;
      }

      @media print {
        body {
          background: #ffffff;
          padding: 0;
        }

        .placard {
          border-radius: 18px;
          min-height: calc(100vh - 0.7in);
        }
      }
    </style>
  </head>
  <body>
    <main class="placard">
      <div class="stripe"></div>
      <img class="logo" src="${logoURL}" alt="Frienzzi" />
      <p class="eyebrow">Frienzzi Garage</p>
      <h1>${escapeHtml(title)}</h1>
      <p class="owner">Shared by ${escapeHtml(ownerName)}</p>
      <div class="qr-wrap" aria-label="QR code">${qrSvg}</div>
      <p class="cta">Scan to see this Garage build</p>
      <p class="url">${escapeHtml(url.replace(/^https?:\/\//, ''))}</p>
      <p class="footer">Real friends. Real builds. frienzzi.com</p>
    </main>
  </body>
</html>`;
}

export async function printGarageVehicleQrPlacard(vehicle: GarageVehicle) {
  const path = garageVehiclePath(vehicle);

  if (!path) {
    throw new Error('This vehicle needs a public Garage URL before printing.');
  }

  const url = `${siteOrigin}${path}`;

  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    await copyTextToClipboard(url);
    return 'copied' as const;
  }

  const printWindow = window.open('', '_blank', 'width=900,height=1100');

  if (!printWindow) {
    throw new Error('Allow pop-ups so Frienzzi can open the print placard.');
  }

  printWindow.document.open();
  printWindow.document.write(printLoadingHtml());
  printWindow.document.close();

  try {
    const qrSvg = await QRCode.toString(url, {
      color: {
        dark: '#1a0c3e',
        light: '#ffffff',
      },
      errorCorrectionLevel: 'H',
      margin: 1,
      type: 'svg',
      width: 520,
    });
    const html = placardHtml({
      ownerName: vehicle.ownerFullName ?? 'Frienzzi user',
      qrSvg,
      title: garageVehicleTitle(vehicle),
      url,
    });

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'Frienzzi could not prepare this Garage QR code.';

    printWindow.document.open();
    printWindow.document.write(printErrorHtml(message));
    printWindow.document.close();
    throw error;
  }

  return 'printed' as const;
}
